import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  init,
  backButton,
  miniApp,
  themeParams,
  viewport,
} from '@telegram-apps/sdk-react';

import App from './app';
import './index.css';

// Promise helper that rejects after `ms` milliseconds. We race the SDK
// bootstrap against it so a hanging `viewport.mount()` (common in plain
// browsers, where Telegram never answers the mount RPC) cannot block the
// React render.
function timeout(ms: number, label: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`timeout: ${label}`)), ms);
  });
}

async function initSDK() {
  init();
  if (miniApp.mountSync.isAvailable()) miniApp.mountSync();
  if (themeParams.mountSync.isAvailable()) themeParams.mountSync();
  if (themeParams.bindCssVars.isAvailable()) themeParams.bindCssVars();

  if (viewport.mount.isAvailable()) {
    await viewport.mount();
    if (viewport.expand.isAvailable()) viewport.expand();
    if (viewport.bindCssVars.isAvailable()) viewport.bindCssVars();
  }
  if (backButton.mount.isAvailable()) backButton.mount();
}

async function bootstrap() {
  if (import.meta.env.DEV) {
    try {
      await import('./mockEnv');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('mockEnv import failed:', err);
    }
  }

  try {
    await Promise.race([initSDK(), timeout(1500, 'sdk-init')]);
  } catch (err) {
    // Running outside Telegram, or the mock didn't answer viewport RPCs in
    // time. Render anyway — the app's `.isAvailable()` guards will short-
    // circuit SDK calls that would otherwise fail.
    // eslint-disable-next-line no-console
    console.warn('Telegram SDK init skipped:', err);
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

void bootstrap();
